import { useState, useEffect } from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useNavigate } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { AlertTriangle, Check, Copy } from 'lucide-react'
import { useProcessorById, useUpdateProcessor } from '@/lib/api/processors'
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id'
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { SecretInput } from '@/components/form/secret-input'
import { useToast } from '@/components/ui/use-toast'
import { config } from '@/lib/config'
import { ProcessorEditHeader } from '../../components/processor-edit-header'
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field'

const BuziPayEditPageInner = () => {
    const id = useProcessorEditId()
    const navigate = useNavigate()
    const { t } = useTranslation('processors')
    const { toast } = useToast()
    const processorExit = useProcessorEditExit()

    const { data: processor, isLoading } = useProcessorById(id)
    const updateProcessorMutation = useUpdateProcessor()

    const [validationError, setValidationError] = useState<string | null>(null)
    const [copiedWebhook, setCopiedWebhook] = useState(false)
    const webhookUrl = `${config.apiBaseUrl}/api/public/webhooks/buzipay`
    const [formData, setFormData] = useState({
        name: '',
        enabled: true,
        apiKey: '',
        webhookSecret: '',
        testMode: false,
        dryMode: false,
    })

    useEffect(() => {
        if (processor) {
            const options = processor.options as any
            setFormData({
                name: processor.name,
                enabled: processor.enabled ?? true,
                apiKey: options?.apiKey || '',
                webhookSecret: options?.webhookSecret || '',
                testMode: options?.testMode === true,
                dryMode: options?.dryMode === true,
            })
        }
    }, [processor])

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            setValidationError(t('nameRequired'))
            return
        }

        if (!formData.apiKey.trim()) {
            setValidationError(t('buzipay.validation.apiKeyRequired'))
            return
        }

        if (!formData.webhookSecret.trim()) {
            setValidationError(t('buzipay.validation.webhookSecretRequired'))
            return
        }

        setValidationError(null)

        try {
            await updateProcessorMutation.mutateAsync({
                processorId: processor!.id,
                updatedData: {
                    type: processor!.type,
                    name: formData.name,
                    enabled: formData.enabled,
                    options: {
                        apiKey: formData.apiKey,
                        webhookSecret: formData.webhookSecret,
                        testMode: formData.testMode,
                        dryMode: formData.dryMode,
                    },
                } as any,
            })

            toast({ description: t('processorUpdated') })
            if (processorExit.isEmbed) {
                processorExit.exitAfterSave()
                return
            }
            navigate(-1)
        } catch {
            toast({ description: t('failedToSave'), variant: 'destructive' })
        }
    }

    const handleCancel = () => {
        if (processorExit.isEmbed) {
            processorExit.exitCancel()
            return
        }
        navigate(-1)
    }

    const handleCopyWebhook = async () => {
        try {
            await navigator.clipboard.writeText(webhookUrl)
            setCopiedWebhook(true)
            setTimeout(() => setCopiedWebhook(false), 2000)
            toast({ description: t('webhookUrlCopied') })
        } catch {
            toast({ description: t('failedToCopyWebhookUrl'), variant: 'destructive' })
        }
    }

    if (isLoading) {
        return <div className="container mx-auto py-8" />
    }

    if (!processor || processor.type !== 'buzipay') {
        return (
            <div className="container mx-auto py-8">
                <Alert variant="destructive">
                    <AlertDescription>{t('processorNotFound')}</AlertDescription>
                </Alert>
            </div>
        )
    }

    return (
        <div className="container mx-auto py-8">
            <ProcessorEditHeader
                processorId={processor.id}
                title={t('buzipay.editTitle')}
                onCancel={handleCancel}
                onSave={handleSubmit}
                isSaving={updateProcessorMutation.isPending}
            />

            {validationError && (
                <Alert variant="destructive" className="mb-6">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{validationError}</AlertDescription>
                </Alert>
            )}

            <div className="space-y-6">
                <Card>
                    <CardHeader>
                        <CardTitle>{t('generalSettings')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="name">{t('processorName')}</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>

                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="enabled"
                                checked={formData.enabled}
                                onCheckedChange={(checked) =>
                                    setFormData({ ...formData, enabled: checked === true })
                                }
                            />
                            <Label htmlFor="enabled">{t('enabled')}</Label>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('apiCredentials')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                            <p className="text-sm text-blue-700">
                                <strong>{t('buzipay.credentials.howToGetTitle')}</strong>
                            </p>
                            <ol className="mt-2 list-decimal pl-4 text-sm text-blue-700">
                                <li>{t('buzipay.credentials.step1')}</li>
                                <li>
                                    <Trans i18nKey="buzipay.credentials.step2" ns="processors" components={{ b: <b /> }} />
                                </li>
                                <li>{t('buzipay.credentials.step3')}</li>
                                <li>{t('buzipay.credentials.step4')}</li>
                            </ol>
                        </div>

                        <SecretInput
                            label={t('buzipay.credentials.apiKeyLabel') + ' *'}
                            value={formData.apiKey}
                            onChange={(value) => setFormData({ ...formData, apiKey: value })}
                            placeholder="sk_live_..."
                        />

                        <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                            <p className="text-sm text-blue-700">
                                <strong>{t('buzipay.webhook.howToGetTitle')}</strong>
                            </p>
                            <ol className="mt-2 list-decimal pl-4 text-sm text-blue-700">
                                <li>{t('buzipay.webhook.step1')}</li>
                                <li>
                                    <Trans i18nKey="buzipay.webhook.step2" ns="processors" components={{ b: <b /> }} />
                                </li>
                                <li>{t('buzipay.webhook.step3')}</li>
                                <li>{t('buzipay.webhook.step4')}</li>
                            </ol>
                        </div>
                        <SecretInput
                            label={t('buzipay.webhook.secretLabel') + ' *'}
                            value={formData.webhookSecret}
                            onChange={(value) => setFormData({ ...formData, webhookSecret: value })}
                            placeholder="whsec_..."
                        />

                        <div className="space-y-2">
                            <Label>{t('webhookUrl')}</Label>
                            <div className="flex items-center gap-2">
                                <Input value={webhookUrl} readOnly className="font-mono text-xs" />
                                <Button type="button" variant="outline" size="icon" onClick={handleCopyWebhook}>
                                    {copiedWebhook ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {t('webhookUrlDescription')}
                            </p>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('testModeSettings')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="testMode"
                                checked={formData.testMode}
                                onCheckedChange={(checked) =>
                                    setFormData({ ...formData, testMode: checked === true })
                                }
                            />
                            <Label htmlFor="testMode">{t('enableTestMode')}</Label>
                        </div>
                    </CardContent>
                </Card>

                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                    disabled={updateProcessorMutation.isPending}
                />
            </div>
        </div>
    )
}

export const BuziPayEditPage = withRequiredPermission(BuziPayEditPageInner, PERMISSIONS.PROCESSORS_MANAGE)
